import type { RegisterToggleBootstrap, RegisterToggleElements, RegisterToggleMode } from './register/toggle-types';
import {
  cacheToggleElements,
  clearLoginNotice,
  createToggleLink,
  setModeText,
  setSubmitText,
  setToggleLinkText,
  setLoginNotice,
  setLoginFormInactive,
} from './register/toggle-dom';
import {
  restoreRegisterFields,
  setFluentFormMode,
  setupMagicLinkFields,
} from './register/toggle-fluent-forms';
import { submitPhantomMagicLinkForm } from './register/toggle-submit';

type PifsterWindow = Window & {
  PIFSTER_REGISTER_TOGGLE?: RegisterToggleBootstrap;
};

type RegisterFormWithHandler = HTMLFormElement & {
  _pifsterLoginHandler?: boolean;
};

const REGISTER_FORM_SELECTOR = 'form.frm-fluent-form, form.ff-form, .ff-form-container form';

const TEXT = {
  registerHeading: 'Turn Small Change into Real Change',
  loginHeading: 'Welcome Back!',
  registerButton: 'Register',
  loginButton: 'Send Magic Link',
  toggleToLogin: 'Already a PIFster? Sign in here',
  toggleToRegister: 'New here? Create an account',
  magicLinkSent: 'Check your email! We sent a sign-in link. Check your inbox (and spam).',
  magicLinkError: 'We couldn’t send your sign-in link right now. Please try again.',
  invalidMagicLink: 'That sign-in link is invalid or expired. Please request a new one.',
  usedMagicLink: 'That sign-in link has already been used. Please request a new one.',
  turnstileError: 'Security verification failed. Please try again.',
} as const;

interface RegisterToggleState {
  mode: RegisterToggleMode;
  originalHeadingText: string | null;
  originalButtonText: string | null;
  redirectTo: string;
  initialized: boolean;
}

const state: RegisterToggleState = {
  mode: 'register',
  originalHeadingText: null,
  originalButtonText: null,
  redirectTo: `${window.location.origin}/profile/`,
  initialized: false,
};

let elements: RegisterToggleElements | null = null;
let retryIntervalId: number | null = null;
let domObserver: MutationObserver | null = null;

function getBootstrapNonce(): string {
  const pifsterWindow = window as PifsterWindow;
  const bootstrap = pifsterWindow.PIFSTER_REGISTER_TOGGLE;
  if (bootstrap?.nonce) {
    return bootstrap.nonce;
  }

  const moduleDataEl = document.getElementById('wp-script-module-data-pifster-register-login-toggle');
  if (!moduleDataEl) {
    return '';
  }

  try {
    const raw = JSON.parse(moduleDataEl.textContent || '{}') as Partial<RegisterToggleBootstrap>;
    if (typeof raw.nonce === 'string' && raw.nonce.trim() !== '') {
      pifsterWindow.PIFSTER_REGISTER_TOGGLE = { nonce: raw.nonce };
      return raw.nonce;
    }
  } catch {
    // No-op: fall back to an empty nonce and let the submit handler fail safely.
  }

  return '';
}

function getRedirectTo(): string {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectToParam = urlParams.get('redirect_to');
    if (redirectToParam) {
      return redirectToParam;
    }

    const formWrapper = document.querySelector<HTMLElement>('[data-pifster-register-login-form]');
    const wrapperRedirect = formWrapper?.dataset['redirectTo'];
    return wrapperRedirect || `${window.location.origin}/profile/`;
  } catch {
    return `${window.location.origin}/profile/`;
  }
}

function getRegisterForm(): HTMLFormElement | null {
  const formWrapper = document.querySelector<HTMLElement>('[data-pifster-register-login-form]');
  if (formWrapper) {
    return formWrapper.querySelector<HTMLFormElement>(REGISTER_FORM_SELECTOR);
  }

  const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
  if (path === 'support' || path.startsWith('support/')) {
    return null;
  }

  return document.querySelector<HTMLFormElement>(REGISTER_FORM_SELECTOR);
}

function updateUrlForMode(mode: RegisterToggleMode): void {
  const url = new URL(window.location.href);
  if (mode === 'login') {
    url.searchParams.set('mode', 'login');
  } else {
    url.searchParams.delete('mode');
  }
  window.history.replaceState({}, '', url.toString());
}

function getElements(): RegisterToggleElements {
  if (!elements) {
    throw new Error('[register-toggle] Elements have not been initialized.');
  }

  return elements;
}

function applyLoginNoticeFromQuery(params: URLSearchParams): void {
  if (state.mode !== 'login') {
    return;
  }

  setLoginFormInactive(false);

  const mlSent = params.get('ml_sent') || '';
  const mlError = params.get('ml_error') || '';
  const mlStatus = params.get('ml_status') || '';

  if (mlSent === '1') {
    setLoginNotice(TEXT.magicLinkSent, 'success');
    setLoginFormInactive(true);
    return;
  }

  if (mlError === 'turnstile') {
    setLoginNotice(TEXT.turnstileError, 'error');
    return;
  }

  if (mlSent === '0') {
    setLoginNotice(TEXT.magicLinkError, 'error');
    return;
  }

  if (mlStatus === 'invalid_intent') {
    setLoginNotice(TEXT.invalidMagicLink, 'error');
    return;
  }

  if (mlStatus === 'already_used') {
    setLoginNotice(TEXT.usedMagicLink, 'error');
    return;
  }

  clearLoginNotice();
}

function toggleToLoginMode(): void {
  const currentElements = getElements();
  state.mode = 'login';

  setFluentFormMode(currentElements, 'login');
  setModeText(
    currentElements,
    'login',
    state.originalHeadingText ?? TEXT.registerHeading,
    TEXT.loginHeading,
  );
  setSubmitText(
    currentElements,
    'login',
    state.originalButtonText ?? TEXT.registerButton,
    TEXT.loginButton,
  );
  setToggleLinkText(currentElements, TEXT.toggleToRegister);

  setupMagicLinkFields(currentElements, state.redirectTo, getBootstrapNonce());
  applyLoginNoticeFromQuery(new URLSearchParams(window.location.search));

  window.setTimeout(() => {
    currentElements.emailFieldEl.focus();
  }, 300);
}

function toggleToRegisterMode(): void {
  const currentElements = getElements();
  state.mode = 'register';

  setFluentFormMode(currentElements, 'register');
  setModeText(
    currentElements,
    'register',
    state.originalHeadingText ?? TEXT.registerHeading,
    TEXT.loginHeading,
  );
  setSubmitText(
    currentElements,
    'register',
    state.originalButtonText ?? TEXT.registerButton,
    TEXT.loginButton,
  );
  setToggleLinkText(currentElements, TEXT.toggleToLogin);

  setLoginFormInactive(false);
  restoreRegisterFields(currentElements);
  clearLoginNotice();
}

function handleToggleClick(event: MouseEvent): void {
  event.preventDefault();

  if (state.mode === 'register') {
    toggleToLoginMode();
  } else {
    toggleToRegisterMode();
  }

  updateUrlForMode(state.mode);
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function handleLoginSubmit(event: Event): void {
  if (state.mode !== 'login') {
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();

  const currentElements = getElements();
  const email = currentElements.emailFieldEl.value.trim();
  if (!email || !isValidEmail(email)) {
    window.alert('Please enter a valid email address.');
    currentElements.emailFieldEl.focus();
    return;
  }

  let token = '';
  if (currentElements.turnstileTokenInputEl) {
    token = String(currentElements.turnstileTokenInputEl.value || '').trim();
  }

  if (!token) {
    const turnstileTokenInput = currentElements.form.querySelector<HTMLInputElement>('input[name="cf-turnstile-response"]');
    if (turnstileTokenInput) {
      token = String(turnstileTokenInput.value || '').trim();
    }
  }

  if (currentElements.turnstileSource !== null && !token) {
    window.alert('Please complete the verification.');
    return;
  }

  const nonce = getBootstrapNonce();
  if (!nonce) {
    console.error('[register-toggle] Missing localized nonce for magic-link submission.');
    window.alert('The login form is missing its security token. Please refresh the page and try again.');
    return;
  }

  submitPhantomMagicLinkForm(email, token, state.redirectTo, nonce);
}

function bindToggleLink(): void {
  const currentElements = getElements();
  if (!currentElements.toggleAnchorEl) {
    return;
  }

  if (currentElements.toggleAnchorEl.dataset['pifsterToggleBound'] === '1') {
    return;
  }

  currentElements.toggleAnchorEl.dataset['pifsterToggleBound'] = '1';
  currentElements.toggleAnchorEl.addEventListener('click', handleToggleClick);
}

function bindLoginSubmitHandler(): void {
  const currentElements = getElements();
  const form = currentElements.form as RegisterFormWithHandler;
  if (form._pifsterLoginHandler) {
    return;
  }

  form._pifsterLoginHandler = true;
  form.addEventListener('submit', handleLoginSubmit, true);
}

function initializeToggle(): boolean {
  if (state.initialized) {
    return true;
  }

  const form = getRegisterForm();
  if (!form) {
    return false;
  }

  try {
    elements = cacheToggleElements(form);
  } catch (error) {
    console.error(error);
    return false;
  }

  const currentElements = getElements();
  state.originalHeadingText = currentElements.headingEl?.textContent ?? state.originalHeadingText;
  state.originalButtonText = currentElements.submitButtonEl?.textContent ?? state.originalButtonText;
  state.redirectTo = getRedirectTo();

  createToggleLink(currentElements);
  bindToggleLink();
  bindLoginSubmitHandler();

  const urlParams = new URLSearchParams(window.location.search);
  const modeParam = urlParams.get('mode');
  const hasLoginFeedback = ['ml_sent', 'ml_error', 'ml_status'].some((key) => urlParams.has(key));
  if (modeParam === 'login' || hasLoginFeedback) {
    toggleToLoginMode();
  } else {
    setToggleLinkText(currentElements, TEXT.toggleToLogin);
  }

  applyLoginNoticeFromQuery(urlParams);
  state.initialized = true;
  return true;
}

function startPolling(): void {
  if (retryIntervalId !== null) {
    return;
  }

  let attempts = 0;
  const maxAttempts = 40;
  retryIntervalId = window.setInterval(() => {
    attempts += 1;
    if (initializeToggle()) {
      if (retryIntervalId !== null) {
        window.clearInterval(retryIntervalId);
        retryIntervalId = null;
      }
      if (domObserver) {
        domObserver.disconnect();
        domObserver = null;
      }
      return;
    }

    if (attempts >= maxAttempts && retryIntervalId !== null) {
      window.clearInterval(retryIntervalId);
      retryIntervalId = null;
    }
  }, 250);
}

function startObserver(): void {
  if (typeof MutationObserver !== 'function' || domObserver !== null) {
    return;
  }

  domObserver = new MutationObserver(() => {
    if (state.initialized) {
      if (domObserver) {
        domObserver.disconnect();
        domObserver = null;
      }
      return;
    }

    if (initializeToggle()) {
      if (retryIntervalId !== null) {
        window.clearInterval(retryIntervalId);
        retryIntervalId = null;
      }
      if (domObserver) {
        domObserver.disconnect();
        domObserver = null;
      }
    }
  });

  domObserver.observe(document.documentElement, { childList: true, subtree: true });
}

function boot(): void {
  if (state.initialized) {
    return;
  }

  if (initializeToggle()) {
    return;
  }

  startPolling();
  startObserver();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
